/* /src/mitras/mitraCategories.jsx
 *
 * Mitra ecosystem metadata — the single source of truth for the five
 * customer-facing Mitra buckets. Used by:
 *   • /mitras hub page (5 category cards)
 *   • /mitras/:bucket filtered listing
 *   • Mitra registration form
 *   • Header / Footer CTAs
 *   • Any future Mitra analytics / SEO routes
 *
 * Each bucket has:
 *   • `key` matching the backend `mitra_category` enum
 *   • `label` (English) + `labelHi` (Hindi devanagari)
 *   • Devotion-style `tagline` line — warm and family-oriented
 *   • Marketing `description` line (1-line subhead)
 *   • Inline `services` list shown on the bucket landing page
 *   • Color `accent`, gradient, and lucide `Icon` for visual identity
 *   • `available` flag — controls whether registration + listing show
 *     the "active" CTA or the "empty state — be the first to register"
 *
 * The five buckets are a stable, branded taxonomy. New sub-categories
 * (e.g. Solar Mitra) plug in by adding a 6th entry here AND a row to
 * `MITRA_CATEGORY_BY_SUBCATEGORY` in backend/professionals.py.
 */
import {
  Compass,
  HardHat,
  Building2,
  Hammer,
  Wallet,
} from "lucide-react";
import { FINANCE_MITRA_ENABLED } from "@/lib/featureFlags";

export const MITRA_BUCKETS = [
  {
    key: "design",
    label: "Design Mitra",
    labelHi: "डिज़ाइन मित्र",
    tagline: "Trusted experts helping families design smarter homes.",
    taglineHi: "घर के सपने को आकार देने वाले अनुभवी मित्र।",
    description:
      "Architects, structural engineers, interior designers and consultants who turn your vision into a buildable plan.",
    services: [
      "Architects",
      "Home Designers",
      "Interior Designers",
      "Structural Engineers",
      "Electrical Consultants",
      "Plumbing Consultants",
      "HVAC Experts",
    ],
    subCategoryKeys: [
      "architect",
      "home_designer",
      "interior",
      "structural",
      "mep",
      "electrical_consultant",
      "plumbing_consultant",
      "hvac_expert",
    ],
    Icon: Compass,
    accent: "#0b2545",
    accentSoft: "rgba(11,37,69,0.07)",
    badgeClass: "bg-navy text-gold border-navy",
    available: true,
    publiclyVisible: true,
  },
  {
    key: "construction",
    label: "Construction Mitra",
    labelHi: "निर्माण मित्र",
    tagline: "Reliable construction partners for quality home execution.",
    taglineHi: "हर ईंट में भरोसा — निर्माण को बनाएँ आसान।",
    description:
      "Contractors, builders, and site supervisors who deliver homes on time, on quality, and within budget.",
    services: [
      "Contractors",
      "Builders",
      "Site Supervisors",
      "Construction Teams",
    ],
    subCategoryKeys: ["contractor", "builder", "site_supervisor", "construction_team"],
    Icon: HardHat,
    accent: "#b46329",
    accentSoft: "rgba(180,99,41,0.10)",
    badgeClass: "bg-orange-700 text-white border-orange-800",
    available: true,
    publiclyVisible: true,
  },
  {
    key: "property",
    label: "Property Mitra",
    labelHi: "संपत्ति मित्र",
    tagline: "Helping families navigate property and approvals with confidence.",
    taglineHi: "ज़मीन, दस्तावेज़ और अनुमोदन — हर कदम पर भरोसा।",
    description:
      "Property dealers, registry & documentation support, and municipal approval consultants.",
    services: [
      "Property Dealers",
      "Registry Assistance",
      "Documentation Support",
      "Municipal Approval Assistance",
      "Property Verification Support",
    ],
    subCategoryKeys: [
      "property_dealer",
      "registry_assistance",
      "documentation_support",
      "municipal_approval",
      "property_verification",
    ],
    Icon: Building2,
    accent: "#5b8c5a",
    accentSoft: "rgba(91,140,90,0.10)",
    badgeClass: "bg-emerald-700 text-white border-emerald-800",
    available: true,
    publiclyVisible: true,
  },
  {
    key: "material",
    label: "Building Material Mitra",
    labelHi: "निर्माण सामग्री मित्र",
    tagline: "Verified suppliers for trusted building materials.",
    taglineHi: "अच्छी सामग्री ही मज़बूत घर की पहली ईंट है।",
    description:
      "Brick, block, steel, cement, tile, paint and hardware suppliers — all verified for quality.",
    services: [
      "Brick & Block Suppliers",
      "Steel Suppliers",
      "Cement Suppliers",
      "Tiles Suppliers",
      "Paint Suppliers",
      "Sanitary Suppliers",
      "Hardware Suppliers",
    ],
    subCategoryKeys: [
      "brick_supplier",
      "block_supplier",
      "steel_supplier",
      "cement_supplier",
      "tiles_supplier",
      "paint_supplier",
      "sanitary_supplier",
      "hardware_supplier",
    ],
    Icon: Hammer,
    accent: "#7b3b8d",
    accentSoft: "rgba(123,59,141,0.10)",
    badgeClass: "bg-fuchsia-700 text-white border-fuchsia-800",
    available: true,
    publiclyVisible: true,
  },
  {
    key: "finance",
    label: "Finance Mitra",
    labelHi: "वित्त मित्र",
    tagline: "Making home financing simpler for every family.",
    taglineHi: "घर के लिए loan और EMI — आसान शब्दों में समझाएँ।",
    description:
      "Home loan, construction loan, EMI guidance and bank coordination support.",
    services: [
      "Home Loan Assistance",
      "Construction Loan Assistance",
      "EMI Guidance",
      "Bank Coordination Support",
    ],
    subCategoryKeys: [
      "home_loan",
      "construction_loan",
      "emi_guidance",
      "bank_coordination",
    ],
    Icon: Wallet,
    accent: "#b8860b",
    accentSoft: "rgba(184,134,11,0.10)",
    badgeClass: "bg-amber-700 text-white border-amber-800",
    available: true,
    // Compliance flag — see /app/frontend/src/lib/featureFlags.js.
    // While false, this bucket is hidden from every public listing,
    // navigation, search filter, and footer. Data + sub-categories
    // are retained intact for one-flag restoration.
    publiclyVisible: FINANCE_MITRA_ENABLED,
  },
];

/**
 * Buckets the public site is allowed to render right now. Use this
 * everywhere a customer can see the list (hub page, header dropdowns,
 * footer, search filters). Admin/professional registration screens
 * may still see all buckets via MITRA_BUCKETS directly.
 */
export const PUBLIC_MITRA_BUCKETS = MITRA_BUCKETS.filter(
  (b) => b.publiclyVisible !== false,
);

export const MITRA_BUCKET_BY_KEY = Object.fromEntries(
  MITRA_BUCKETS.map((b) => [b.key, b])
);

// Sub-category → Mitra bucket key (mirrors backend MITRA_CATEGORY_BY_SUBCATEGORY).
// Used to render the right colour/icon on existing listing cards that still
// carry the precise `category` value (e.g. "architect").
export const MITRA_BUCKET_BY_SUBCATEGORY = MITRA_BUCKETS.reduce((acc, bucket) => {
  for (const sub of bucket.subCategoryKeys) {
    acc[sub] = bucket.key;
  }
  return acc;
}, {});

export const mitraBucket = (subCategory) =>
  MITRA_BUCKET_BY_KEY[MITRA_BUCKET_BY_SUBCATEGORY[subCategory] || "design"];

export const mitraBucketLabel = (key) =>
  MITRA_BUCKET_BY_KEY[key]?.label || "Mitra";

// Human-friendly label for every Mitra sub-category. Used by the
// registration form and the admin panel.
export const MITRA_SUBCATEGORY_LABEL = {
  // Design Mitra
  architect: "Architect",
  home_designer: "Home Designer",
  interior: "Interior Designer",
  structural: "Structural Engineer",
  mep: "MEP Engineer",
  electrical_consultant: "Electrical Consultant",
  plumbing_consultant: "Plumbing Consultant",
  hvac_expert: "HVAC Expert",
  // Construction Mitra
  contractor: "Contractor",
  builder: "Builder",
  site_supervisor: "Site Supervisor",
  construction_team: "Construction Team",
  // Property Mitra
  property_dealer: "Property Dealer",
  registry_assistance: "Registry Assistance",
  documentation_support: "Documentation Support",
  municipal_approval: "Municipal Approval Consultant",
  property_verification: "Property Verification",
  // Building Material Mitra
  brick_supplier: "Brick Supplier",
  block_supplier: "Block Supplier",
  steel_supplier: "Steel Supplier",
  cement_supplier: "Cement Supplier",
  tiles_supplier: "Tiles Supplier",
  paint_supplier: "Paint Supplier",
  sanitary_supplier: "Sanitary Supplier",
  hardware_supplier: "Hardware Supplier",
  // Finance Mitra
  home_loan: "Home Loan Advisor",
  construction_loan: "Construction Loan Advisor",
  emi_guidance: "EMI Guidance Expert",
  bank_coordination: "Bank Coordination Expert",
  // Legacy reserved
  vastu: "Vastu Consultant",
  landscape: "Landscape Designer",
};

export const mitraSubCategoryLabel = (key) =>
  MITRA_SUBCATEGORY_LABEL[key] || "Other";
